import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/api';

/**
 * Lead Management (Nov 2026, Phase 1) — shared bits for the Leads page:
 * Drawer, Walk-in form, Phone Call Form, Add Task, Complete Task, stage pill, formatters.
 * Server: /api/leads (server/routes/leads.js).
 */

export const C = { brand: '#c88346', brandSoft: '#fdf3e8', border: '#ece0d1', text: '#3d2b1f', muted: '#7a6b5d', faint: '#a69889', bg: '#faf6f1' };

export const STAGE_COLORS = {
  new: ['#eff6ff', '#1d4ed8'], followup_pending: ['#fffbeb', '#b45309'], ringing: ['#fff7ed', '#c2410c'],
  interested: ['#f5f3ff', '#6d28d9'], customer: ['#f0fdf4', '#15803d']
};
export function StagePill({ stage, label }) {
  const [bg, fg] = STAGE_COLORS[stage] || ['#f3f4f6', '#4b5563'];
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: '12px', background: bg, color: fg, fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>{label || stage}</span>;
}

// Short source tag so Sage leads stand apart from walk-ins (full source name on hover).
const SOURCE_TAGS = {
  'Sage – Find my store': ['Sage', '#ede9fe', '#6d28d9'],
  'Store walk-in': ['Walk-in', '#f3f4f6', '#4b5563'],
  'Website – Connect with store': ['Website', '#e0f2fe', '#0369a1'],
  'Pop-ins video call': ['Pop-ins', '#ccfbf1', '#0f766e']
};
export function SourceTag({ source }) {
  const [label, bg, fg] = SOURCE_TAGS[source] || [source || '—', '#f3f4f6', '#4b5563'];
  return <span title={source} style={{ display: 'inline-block', padding: '2px 9px', borderRadius: '12px', background: bg, color: fg, fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</span>;
}

export const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '';
export const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
export const fmtINR = (n) => (n == null || n === '' ? '' : '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 }));
export const leadName = (l) => [l?.first_name, l?.last_name].filter(Boolean).join(' ');
// <input type="datetime-local"> value (device local time) ⇄ Date
export const toLocalInput = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16); };
export const fromLocalInput = (v) => (v ? new Date(v).toISOString() : null);
export const errOf = (e, dflt) => e?.response?.data?.error || dflt;

export const inputStyle = { width: '100%', padding: '8px 10px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', color: C.text, background: '#fff', boxSizing: 'border-box', outline: 'none' };
export const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 700, color: C.muted, margin: '10px 0 4px', textTransform: 'uppercase', letterSpacing: '0.4px' };
export const btn = (kind = 'primary', disabled = false) => ({
  padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
  border: kind === 'primary' ? 'none' : `1px solid ${C.border}`,
  background: disabled ? '#e5e7eb' : kind === 'primary' ? C.brand : kind === 'danger' ? '#fef2f2' : '#fff',
  color: disabled ? '#9ca3af' : kind === 'primary' ? '#fff' : kind === 'danger' ? '#b91c1c' : C.text
});
const Req = () => <span style={{ color: '#dc2626' }}> *</span>;

// Right-side drawer (full screen on narrow devices).
// Closes only via × / Cancel: a click on the backdrop must not throw away a half-filled form.
export function Drawer({ title, onClose, children, footer, width = 460 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.35)', display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: `min(${width}px, 100vw)`, height: '100%', background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 30px rgba(0,0,0,0.15)' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1, fontSize: '16px', fontWeight: 800, color: C.text }}>{title}</div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: '20px', cursor: 'pointer', color: C.muted }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 20px 20px' }}>{children}</div>
        {footer && <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>{footer}</div>}
      </div>
    </div>
  );
}

export function ErrorBox({ text }) {
  if (!text) return null;
  return <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '8px 10px', borderRadius: '8px', fontSize: '12.5px', marginTop: '10px' }}>{text}</div>;
}

// Multi-select of POS catalogue product titles (search as you type).
export function ProductPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get('/leads/products', { params: { q } }).then(r => setOpts(r.data.products || [])).catch(() => setOpts([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const toggle = (p) => onChange(value.includes(p) ? value.filter(x => x !== p) : [...value, p]);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: value.length ? '6px' : 0 }}>
        {value.map(p => (
          <span key={p} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: C.brandSoft, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '2px 8px', fontSize: '12px', color: C.text }}>
            {p}<button onClick={() => toggle(p)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.muted, padding: 0 }}>×</button>
          </span>
        ))}
      </div>
      <input value={q} onChange={e => setQ(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search products (Cloud, Latex, Recliner…)" style={inputStyle} />
      {open && opts.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 5, left: 0, right: 0, top: '100%', marginTop: '2px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: '8px', maxHeight: '220px', overflowY: 'auto', boxShadow: '0 8px 20px rgba(0,0,0,0.08)' }}>
          {opts.map(p => (
            <div key={p} onMouseDown={e => { e.preventDefault(); toggle(p); }} style={{ padding: '7px 10px', fontSize: '13px', cursor: 'pointer', display: 'flex', gap: '8px', alignItems: 'center', background: value.includes(p) ? C.brandSoft : '#fff' }}>
              <input type="checkbox" readOnly checked={value.includes(p)} style={{ accentColor: C.brand }} />{p}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function OwnerSelect({ storeId, value, onChange, allowBlank = true }) {
  const [owners, setOwners] = useState([]);
  useEffect(() => {
    if (!storeId) return;
    api.get('/leads/owners', { params: { storeId } }).then(r => setOwners(r.data.owners || [])).catch(() => setOwners([]));
  }, [storeId]);
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle}>
      {allowBlank && <option value="">Unassigned (store)</option>}
      {value && !owners.some(o => o.username === value) && <option value={value}>{value}</option>}
      {owners.map(o => <option key={o.username} value={o.username}>{o.name && o.name !== o.username ? `${o.name} (${o.username})` : o.username}</option>)}
    </select>
  );
}

// ── Add Walk-in Lead ─────────────────────────────────────────────────────────
// `stores` (managers on "All Stores"): show a Store picker; otherwise the lead goes to `storeId`.
export function WalkInDrawer({ storeId: fixedStoreId, stores, me, onClose, onSaved, onOpenLead }) {
  const [storeId, setStoreId] = useState(fixedStoreId || '');
  const [f, setF] = useState({ phone: '', firstName: '', lastName: '', email: '', dob: '', address: '', pincode: '', city: '', state: '', expectedOrderValue: '', firstResponseAt: toLocalInput(new Date()), notes: '', ownerStaff: me || '' });
  const [products, setProducts] = useState([]);
  const [dnd, setDnd] = useState({ call: false, email: false, sms: false, track: false });
  const [existing, setExisting] = useState(null);
  const [phoneBad, setPhoneBad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));

  const checkPhone = (sid = storeId) => {
    if (!f.phone.trim() || !sid) { setExisting(null); setPhoneBad(false); return; }
    api.get('/leads/check-phone', { params: { storeId: sid, phone: f.phone } })
      .then(r => { setPhoneBad(!r.data.valid); setExisting(r.data.lead); })
      .catch(() => {});
  };
  const save = async () => {
    if (!storeId) { setError('Choose the store for this lead.'); return; }
    setSaving(true); setError('');
    try {
      const r = await api.post('/leads', { ...f, storeId, productsInterested: products, dnd, firstResponseAt: fromLocalInput(f.firstResponseAt) });
      onSaved(r.data);
    } catch (e) { setError(errOf(e, 'Could not save the lead')); }
    setSaving(false);
  };
  return (
    <Drawer title="Add Walk-in Lead" onClose={onClose} width={520}
      footer={<><button onClick={onClose} style={btn('secondary')}>Cancel</button><button onClick={save} disabled={saving} style={btn('primary', saving)}>{saving ? 'Saving…' : existing ? 'Log re-enquiry' : 'Save lead'}</button></>}>
      {stores && (<>
        <label style={labelStyle}>Store<Req /></label>
        <select value={storeId} onChange={e => { setStoreId(e.target.value); checkPhone(e.target.value); }} style={inputStyle}>
          <option value="">Choose store…</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </>)}
      <label style={labelStyle}>Mobile number<Req /></label>
      <input value={f.phone} onChange={set('phone')} onBlur={() => checkPhone()} placeholder="10-digit mobile" inputMode="tel" style={inputStyle} autoFocus />
      {phoneBad && <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '4px' }}>Enter a valid 10-digit Indian mobile number.</div>}
      {existing && (
        <div style={{ marginTop: '8px', padding: '10px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', fontSize: '12.5px', color: '#92400e' }}>
          <b>{existing.lead_code}</b> — {leadName(existing)} already exists at this store ({existing.stage.replace(/_/g, ' ')}, {existing.source}).
          Saving adds a "re-enquired" entry to that lead instead of a new one.
          <div style={{ marginTop: '6px' }}><button onClick={() => onOpenLead(existing.id)} style={btn('secondary')}>Open existing lead</button></div>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
        <div><label style={labelStyle}>First name<Req /></label><input value={f.firstName} onChange={set('firstName')} style={inputStyle} /></div>
        <div><label style={labelStyle}>Last name</label><input value={f.lastName} onChange={set('lastName')} style={inputStyle} /></div>
        <div><label style={labelStyle}>Email</label><input value={f.email} onChange={set('email')} type="email" style={inputStyle} /></div>
        <div><label style={labelStyle}>Date of birth</label><input value={f.dob} onChange={set('dob')} type="date" style={inputStyle} /></div>
      </div>
      <label style={labelStyle}>Address</label>
      <input value={f.address} onChange={set('address')} style={inputStyle} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 10px' }}>
        <div><label style={labelStyle}>Pincode<Req /></label><input value={f.pincode} onChange={set('pincode')} inputMode="numeric" maxLength={6} style={inputStyle} /></div>
        <div><label style={labelStyle}>City</label><input value={f.city} onChange={set('city')} style={inputStyle} /></div>
        <div><label style={labelStyle}>State</label><input value={f.state} onChange={set('state')} style={inputStyle} /></div>
      </div>
      <label style={labelStyle}>Products interested<Req /></label>
      <ProductPicker value={products} onChange={setProducts} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
        <div><label style={labelStyle}>Expected order total (₹)<Req /></label><input value={f.expectedOrderValue} onChange={set('expectedOrderValue')} inputMode="decimal" style={inputStyle} /></div>
        <div><label style={labelStyle}>First response<Req /></label><input value={f.firstResponseAt} onChange={set('firstResponseAt')} type="datetime-local" style={inputStyle} /></div>
      </div>
      <label style={labelStyle}>Owner</label>
      <OwnerSelect storeId={storeId} value={f.ownerStaff} onChange={v => setF(s => ({ ...s, ownerStaff: v }))} />
      <label style={labelStyle}>Do not contact</label>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '13px', color: C.text }}>
        {[['call', 'Call'], ['email', 'Email'], ['sms', 'SMS'], ['track', 'Track']].map(([k, l]) => (
          <label key={k} style={{ display: 'flex', gap: '5px', alignItems: 'center', cursor: 'pointer' }}><input type="checkbox" checked={dnd[k]} onChange={e => setDnd(d => ({ ...d, [k]: e.target.checked }))} style={{ accentColor: C.brand }} />{l}</label>
        ))}
      </div>
      <label style={labelStyle}>Notes<Req /></label>
      <textarea value={f.notes} onChange={set('notes')} maxLength={2000} rows={4} style={{ ...inputStyle, resize: 'vertical' }} placeholder="What the customer is looking for, budget, sizes…" />
      <ErrorBox text={error} />
    </Drawer>
  );
}

// ── Phone Call Form ──────────────────────────────────────────────────────────
export function CallFormDrawer({ lead, meta, completeTaskId, onClose, onSaved }) {
  const [status, setStatus] = useState('');
  const [disposition, setDisposition] = useState('');
  const [stage, setStage] = useState('');
  const [remarks, setRemarks] = useState('');
  const [taskAt, setTaskAt] = useState('');
  const [products, setProducts] = useState(lead.products_interested || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const options = useMemo(() => (meta?.dispositions || []).filter(d => d.call_status === status), [meta, status]);
  const disp = options.find(d => d.code === disposition);
  const taskLabel = disp?.task_type ? ((meta.taskTypes.find(t => t.code === disp.task_type) || {}).label || disp.task_type) : '';

  // Disposition → stage (editable) and a default task time (PRD offsets; "staff picks" starts at +1 day).
  useEffect(() => {
    if (!disp) { setStage(''); setTaskAt(''); return; }
    setStage(disp.sets_stage);
    if (disp.task_type) setTaskAt(toLocalInput(new Date(Date.now() + (disp.task_offset_hours != null ? disp.task_offset_hours : 24) * 3600000)));
    else setTaskAt('');
  }, [disposition]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await api.post(`/leads/${lead.id}/call`, { callStatus: status, disposition, stage, remarks, taskAt: disp?.task_type ? fromLocalInput(taskAt) : null, productsInterested: products, completeTaskId });
      const message = r.data.taskId && taskAt ? `Call saved · ${taskLabel} task created for ${fmtDateTime(fromLocalInput(taskAt))}` : 'Call saved';
      onSaved({ ...r.data, message });
    } catch (e) { setError(errOf(e, 'Could not save the call')); }
    setSaving(false);
  };
  const chip = (on) => ({ flex: 1, padding: '9px', borderRadius: '8px', border: `1.5px solid ${on ? C.brand : C.border}`, background: on ? C.brandSoft : '#fff', color: on ? C.brand : C.text, fontWeight: 700, fontSize: '13px', cursor: 'pointer' });
  const stageOptions = (meta?.stages || []).filter(s => s.code !== 'customer');
  return (
    <Drawer title={`Phone call — ${leadName(lead)}`} onClose={onClose}
      footer={<><button onClick={onClose} style={btn('secondary')}>Cancel</button><button onClick={save} disabled={saving || !disposition} style={btn('primary', saving || !disposition)}>{saving ? 'Saving…' : 'Save call'}</button></>}>
      <div style={{ fontSize: '12.5px', color: C.muted, marginTop: '8px' }}>+91 {lead.phone} · {lead.lead_code}</div>
      {lead.dnd_call ? <div style={{ marginTop: '8px', fontSize: '12px', color: '#b91c1c', fontWeight: 700 }}>⚠️ This customer asked not to be called.</div> : null}
      <label style={labelStyle}>Call status<Req /></label>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button onClick={() => { setStatus('connected'); setDisposition(''); }} style={chip(status === 'connected')}>Connected</button>
        <button onClick={() => { setStatus('not_connected'); setDisposition(''); }} style={chip(status === 'not_connected')}>Not connected</button>
      </div>
      {status && (<>
        <label style={labelStyle}>Call remark<Req /></label>
        <select value={disposition} onChange={e => setDisposition(e.target.value)} style={inputStyle}>
          <option value="">Choose…</option>
          {options.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
      </>)}
      {disp && (<>
        <label style={labelStyle}>Stage</label>
        <select value={stage} onChange={e => setStage(e.target.value)} style={inputStyle}>
          {stageOptions.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
        </select>
        {disp.task_type ? (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: '8px', fontSize: '12.5px', color: '#155e75' }}>
            📅 <b>{taskLabel}</b> task will be created for <b>{taskAt ? fmtDateTime(fromLocalInput(taskAt)) : '…'}</b>
            <input type="datetime-local" value={taskAt} onChange={e => setTaskAt(e.target.value)} style={{ ...inputStyle, marginTop: '6px' }} />
          </div>
        ) : (
          <div style={{ marginTop: '10px', fontSize: '12px', color: C.faint }}>No follow-up task for this remark.</div>
        )}
        {disp.sets_dnd ? <div style={{ marginTop: '8px', fontSize: '12px', color: '#b91c1c' }}>Sets "Do not call" on the lead.</div> : null}
        {disp.prompt_link_order ? <div style={{ marginTop: '8px', fontSize: '12px', color: '#92400e' }}>After saving, link the order placed under the other number (Link order).</div> : null}
      </>)}
      <label style={labelStyle}>Notes</label>
      <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={3} maxLength={2000} style={{ ...inputStyle, resize: 'vertical' }} />
      <label style={labelStyle}>Products interested</label>
      <ProductPicker value={products} onChange={setProducts} />
      <ErrorBox text={error} />
    </Drawer>
  );
}

// ── Add Task ─────────────────────────────────────────────────────────────────
export function TaskDrawer({ lead, meta, onClose, onSaved }) {
  const [type, setType] = useState('');
  const [owner, setOwner] = useState(lead.owner_staff || meta?.username || '');
  const [at, setAt] = useState(toLocalInput(new Date(Date.now() + 86400000)));
  const [reminder, setReminder] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const types = meta?.taskTypes || [];
  const tt = types.find(t => t.code === type);
  useEffect(() => { if (tt && tt.default_offset_hours != null) setAt(toLocalInput(new Date(Date.now() + tt.default_offset_hours * 3600000))); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true); setError('');
    try {
      await api.post(`/leads/${lead.id}/tasks`, { taskType: type, ownerStaff: owner, scheduledAt: fromLocalInput(at), reminderMinutes: reminder === '' ? null : Number(reminder), description });
      onSaved();
    } catch (e) { setError(errOf(e, 'Could not add the task')); }
    setSaving(false);
  };
  return (
    <Drawer title="Add task" onClose={onClose}
      footer={<><button onClick={onClose} style={btn('secondary')}>Cancel</button><button onClick={save} disabled={saving || !type} style={btn('primary', saving || !type)}>{saving ? 'Saving…' : 'Save & close'}</button></>}>
      <label style={labelStyle}>Task type<Req /></label>
      <select value={type} onChange={e => setType(e.target.value)} style={inputStyle}>
        <option value="">Choose…</option>
        {[['appointment', 'Appointment'], ['todo', 'To-do']].map(([g, gl]) => (
          <optgroup key={g} label={gl}>{types.filter(t => t.task_group === g).map(t => <option key={t.code} value={t.code}>{t.label}</option>)}</optgroup>
        ))}
      </select>
      {tt && <div style={{ fontSize: '12px', color: C.muted, marginTop: '6px' }}>Subject: <b>{tt.label} : {leadName(lead)}</b></div>}
      <label style={labelStyle}>Owner</label>
      <OwnerSelect storeId={lead.store_id} value={owner} onChange={setOwner} />
      <label style={labelStyle}>Date & time<Req /></label>
      <input type="datetime-local" value={at} onChange={e => setAt(e.target.value)} style={inputStyle} />
      <label style={labelStyle}>Reminder</label>
      <select value={reminder} onChange={e => setReminder(e.target.value)} style={inputStyle}>
        <option value="">No reminder</option>
        <option value="0">At the time</option>
        <option value="15">15 minutes before</option>
        <option value="60">1 hour before</option>
        <option value="120">2 hours before</option>
        <option value="1440">1 day before</option>
      </select>
      <label style={labelStyle}>Description</label>
      <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
      <ErrorBox text={error} />
    </Drawer>
  );
}

// ── Complete task (outcome required; Rescheduled creates the next task) ──────
export function CompleteTaskModal({ task, onClose, onSaved }) {
  const [outcome, setOutcome] = useState('done');
  const [notes, setNotes] = useState('');
  const [nextAt, setNextAt] = useState(toLocalInput(new Date(Date.now() + 86400000)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try {
      await api.post(`/leads/tasks/${task.id}/complete`, { outcome, notes, nextAt: outcome === 'rescheduled' ? fromLocalInput(nextAt) : null });
      onSaved();
    } catch (e) { setError(errOf(e, 'Could not complete the task')); }
    setSaving(false);
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(15,23,42,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', padding: '18px 20px', width: '380px', maxWidth: '92vw', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: C.text }}>Complete task</div>
        <div style={{ fontSize: '12.5px', color: C.muted, marginTop: '3px' }}>{task.subject} · {fmtDateTime(task.scheduled_at)}</div>
        <label style={labelStyle}>Outcome</label>
        {[['done', 'Done'], ['no_show', 'Customer no-show'], ['rescheduled', 'Rescheduled']].map(([k, l]) => (
          <label key={k} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', padding: '4px 0', cursor: 'pointer' }}>
            <input type="radio" checked={outcome === k} onChange={() => setOutcome(k)} style={{ accentColor: C.brand }} />{l}
          </label>
        ))}
        {outcome === 'rescheduled' && (<><label style={labelStyle}>New date & time</label><input type="datetime-local" value={nextAt} onChange={e => setNextAt(e.target.value)} style={inputStyle} /></>)}
        <label style={labelStyle}>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
        <ErrorBox text={error} />
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button onClick={onClose} style={btn('secondary')}>Cancel</button>
          <button onClick={save} disabled={saving} style={btn('primary', saving)}>{saving ? 'Saving…' : 'Complete'}</button>
        </div>
      </div>
    </div>
  );
}

// Click-to-call: opens tel: and, when the staff come back to the POS window, the Phone Call Form.
export function useCallReturn(onReturn) {
  const pending = useRef(null);
  useEffect(() => {
    const onFocus = () => {
      const p = pending.current;
      if (p && Date.now() - p.at < 30 * 60000) { pending.current = null; setTimeout(() => onReturn(p), 300); }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [onReturn]);
  return (info) => { pending.current = { ...info, at: Date.now() }; };
}
